import React from 'react'

export const SETUP_TYPE_LABELS = {
  workshop_current: 'Workshop Current',
  manufacturer_baseline: 'Manufacturer Baseline',
  workshop_baseline: 'Workshop Baseline',
  rider_variant: 'Rider Variant',
  recommended_change: 'Recommended Change',
}

export const SETUP_TYPE_OPTIONS = Object.entries(SETUP_TYPE_LABELS).map(([value, label]) => ({ value, label }))

export const SETUP_EMPTY = {
  name: '',
  setup_type: 'workshop_current',
  is_active: false,
  terrain_label: '',
  front_air_pressure: '',
  front_spring_rate: '',
  front_sag_mm: '',
  front_sag_pct: '',
  fork_tokens: '',
  fork_lsc: '',
  fork_hsc: '',
  fork_lsr: '',
  fork_hsr: '',
  fork_mode: '',
  fork_hbo: '',
  rear_air_pressure: '',
  rear_spring_rate: '',
  rear_sag_mm: '',
  rear_sag_pct: '',
  shock_preload_turns: '',
  shock_spacers: '',
  shock_lsc: '',
  shock_hsc: '',
  shock_rebound: '',
  shock_mode: '',
  shock_hbo: '',
  tyre_pressure_front: '',
  tyre_pressure_rear: '',
  notes: '',
}

const NUMERIC_FIELDS = new Set([
  'front_air_pressure',
  'front_sag_mm',
  'front_sag_pct',
  'rear_air_pressure',
  'rear_sag_mm',
  'rear_sag_pct',
  'shock_preload_turns',
  'tyre_pressure_front',
  'tyre_pressure_rear',
])

const INTEGER_FIELDS = new Set([
  'fork_tokens',
  'fork_lsc',
  'fork_hsc',
  'fork_lsr',
  'fork_hsr',
  'shock_spacers',
  'shock_lsc',
  'shock_hsc',
  'shock_rebound',
])

const TEXT_FIELDS = new Set([
  'name',
  'setup_type',
  'terrain_label',
  'front_spring_rate',
  'fork_mode',
  'fork_hbo',
  'rear_spring_rate',
  'shock_mode',
  'shock_hbo',
  'notes',
])

export function setupTypeLabel(type) {
  return SETUP_TYPE_LABELS[type] || String(type || 'Setup').replace(/_/g, ' ')
}

export function defaultSetupName(type = 'workshop_current') {
  if (type === 'workshop_current') return 'Workshop Current'
  if (type === 'workshop_baseline') return 'Workshop Baseline'
  if (type === 'manufacturer_baseline') return 'Manufacturer Baseline'
  if (type === 'rider_variant') return 'My Setup'
  return setupTypeLabel(type)
}

export function toSetupForm(row = null, defaults = {}) {
  const next = { ...SETUP_EMPTY, ...defaults }
  if (!row) return next
  Object.keys(SETUP_EMPTY).forEach(key => {
    if (row[key] !== null && row[key] !== undefined) next[key] = String(row[key])
  })
  next.is_active = Boolean(row.is_active)
  return next
}

function numberOrNull(value) {
  if (value === '' || value === null || value === undefined) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function intOrNull(value) {
  const parsed = numberOrNull(value)
  return parsed === null ? null : Math.round(parsed)
}

function textOrNull(value) {
  const clean = String(value || '').trim()
  return clean || null
}

export function setupPayloadFromForm(form = {}) {
  const payload = {}
  Object.keys(SETUP_EMPTY).forEach(key => {
    if (key === 'is_active') {
      payload.is_active = Boolean(form.is_active)
    } else if (NUMERIC_FIELDS.has(key)) {
      payload[key] = numberOrNull(form[key])
    } else if (INTEGER_FIELDS.has(key)) {
      payload[key] = intOrNull(form[key])
    } else if (TEXT_FIELDS.has(key)) {
      payload[key] = textOrNull(form[key])
    }
  })
  payload.name = payload.name || defaultSetupName(payload.setup_type)
  payload.setup_type = payload.setup_type || 'workshop_current'
  return payload
}

function Field({ label, children }) {
  return <div className="field"><label>{label}</label>{children}</div>
}

function InputField({ label, value, onChange, type = 'text', disabled = false, placeholder = '' }) {
  return (
    <Field label={label}>
      <input
        type={type}
        inputMode={type === 'number' ? 'decimal' : undefined}
        value={value || ''}
        onChange={event => onChange(event.target.value)}
        disabled={disabled}
        placeholder={placeholder}
      />
    </Field>
  )
}

function displaySettingValue(value, unit = '') {
  const text = String(value ?? '').trim()
  const cleanUnit = String(unit || '').trim()
  if (!text || !cleanUnit) return text
  if (cleanUnit === '%' && text.includes('%')) return text
  if (text.toLowerCase().includes(cleanUnit.toLowerCase())) return text
  return cleanUnit === '%' ? `${text}%` : `${text} ${cleanUnit}`
}

export function SetupRecordSummary({ record }) {
  const snapshotSettings = Array.isArray(record.setup_settings)
    ? record.setup_settings.filter(item => item && item.value !== null && item.value !== undefined && item.value !== '')
    : []
  if (snapshotSettings.length) {
    return (
      <div className="portal-mini-grid">
        {snapshotSettings.map(item => (
          <div key={item.key || item.label}>
            <span>{item.label || item.key}</span>
            <strong>{displaySettingValue(item.value, item.unit)}</strong>
          </div>
        ))}
      </div>
    )
  }

  const rows = [
    ['Terrain', record.terrain_label],
    ['Fork PSI / Spring', record.front_air_pressure || record.front_spring_rate],
    ['Fork clicks', [record.fork_lsc && `LSC ${record.fork_lsc}`, record.fork_hsc && `HSC ${record.fork_hsc}`, record.fork_lsr && `LSR ${record.fork_lsr}`, record.fork_hsr && `HSR ${record.fork_hsr}`].filter(Boolean).join(' / ')],
    ['Fork HBO / HBC / ABO', record.fork_hbo],
    ['Shock PSI / Spring', record.rear_air_pressure || record.rear_spring_rate],
    ['Shock clicks', [record.shock_lsc && `LSC ${record.shock_lsc}`, record.shock_hsc && `HSC ${record.shock_hsc}`, record.shock_rebound && `Rebound ${record.shock_rebound}`].filter(Boolean).join(' / ')],
    ['Shock HBO / HBC / ABO', record.shock_hbo],
    ['Tyres', [record.tyre_pressure_front && `F ${record.tyre_pressure_front}`, record.tyre_pressure_rear && `R ${record.tyre_pressure_rear}`].filter(Boolean).join(' / ')],
  ].filter(([, value]) => value !== null && value !== undefined && value !== '')

  return rows.length ? (
    <div className="portal-mini-grid">
      {rows.map(([label, value]) => (
        <div key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  ) : (
    <div className="text-muted fs12">No setup values recorded yet.</div>
  )
}

export default function SetupRecordForm({
  form,
  setForm,
  canEdit = true,
  canChooseType = true,
  allowedTypes = SETUP_TYPE_OPTIONS.map(option => option.value),
  componentType = '',
  onSave,
  onCancel,
  saving = false,
  submitLabel = 'Save Setup Record',
}) {
  function setField(field, value) {
    setForm(current => ({ ...current, [field]: value }))
  }

  const types = SETUP_TYPE_OPTIONS.filter(option => allowedTypes.includes(option.value))
  const normalisedComponentType = String(componentType || '').toLowerCase()
  const showForkSetup = normalisedComponentType ? normalisedComponentType === 'fork' : true
  const showShockSetup = normalisedComponentType ? normalisedComponentType === 'shock' : true
  const canMarkActive = form.setup_type === 'workshop_current' || form.setup_type === 'rider_variant'
  const activeHelp = form.setup_type === 'rider_variant'
    ? 'Mark this as the setup you are currently riding for this component.'
    : 'Only one active workshop current setup is allowed per component.'

  return (
    <form onSubmit={onSave}>
      <div className="grid-2">
        <InputField label="Setup Name" value={form.name} onChange={value => setField('name', value)} disabled={!canEdit} placeholder="Local Flow, Wet Setup..." />
        {canChooseType ? (
          <Field label="Setup Type">
            <select value={form.setup_type || 'workshop_current'} onChange={event => setField('setup_type', event.target.value)} disabled={!canEdit}>
              {types.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </Field>
        ) : (
          <InputField label="Setup Type" value={setupTypeLabel(form.setup_type)} onChange={() => {}} disabled />
        )}
      </div>

      <div className="grid-2">
        <InputField label="Terrain / Conditions" value={form.terrain_label} onChange={value => setField('terrain_label', value)} disabled={!canEdit} placeholder="Local Flow, wet roots, bike park..." />
        <Field label="Active Current Setup">
          <label className="check-item" style={{ marginBottom: 0 }}>
            <input
              type="checkbox"
              checked={Boolean(form.is_active)}
              onChange={event => setField('is_active', event.target.checked)}
              disabled={!canEdit || !canMarkActive}
            />
            <span className="text-muted fs12">{activeHelp}</span>
          </label>
        </Field>
      </div>

      {showForkSetup ? (
        <div className="card compact-card" style={{ background: 'var(--black)' }}>
          <div className="sec" style={{ marginTop: 0 }}>Fork Setup</div>
          <div className="grid-4">
            <InputField label="Air Pressure" type="number" value={form.front_air_pressure} onChange={value => setField('front_air_pressure', value)} disabled={!canEdit} />
            <InputField label="Spring Rate" value={form.front_spring_rate} onChange={value => setField('front_spring_rate', value)} disabled={!canEdit} placeholder="e.g. 50 lb/in" />
            <InputField label="Sag MM" type="number" value={form.front_sag_mm} onChange={value => setField('front_sag_mm', value)} disabled={!canEdit} />
            <InputField label="Sag %" type="number" value={form.front_sag_pct} onChange={value => setField('front_sag_pct', value)} disabled={!canEdit} />
          </div>
          <div className="grid-4">
            <InputField label="Tokens" type="number" value={form.fork_tokens} onChange={value => setField('fork_tokens', value)} disabled={!canEdit} />
            <InputField label="LSC" type="number" value={form.fork_lsc} onChange={value => setField('fork_lsc', value)} disabled={!canEdit} />
            <InputField label="HSC" type="number" value={form.fork_hsc} onChange={value => setField('fork_hsc', value)} disabled={!canEdit} />
            <InputField label="Fork Mode" value={form.fork_mode} onChange={value => setField('fork_mode', value)} disabled={!canEdit} />
          </div>
          <div className="grid-3">
            <InputField label="LSR / Rebound" type="number" value={form.fork_lsr} onChange={value => setField('fork_lsr', value)} disabled={!canEdit} />
            <InputField label="HSR" type="number" value={form.fork_hsr} onChange={value => setField('fork_hsr', value)} disabled={!canEdit} />
            <InputField label="HBO / HBC / ABO" value={form.fork_hbo} onChange={value => setField('fork_hbo', value)} disabled={!canEdit} />
          </div>
        </div>
      ) : null}

      {showShockSetup ? (
        <div className="card compact-card" style={{ background: 'var(--black)' }}>
          <div className="sec" style={{ marginTop: 0 }}>Shock Setup</div>
          <div className="grid-4">
            <InputField label="Air Pressure" type="number" value={form.rear_air_pressure} onChange={value => setField('rear_air_pressure', value)} disabled={!canEdit} />
            <InputField label="Spring Rate" value={form.rear_spring_rate} onChange={value => setField('rear_spring_rate', value)} disabled={!canEdit} placeholder="e.g. 450 lb/in" />
            <InputField label="Sag MM" type="number" value={form.rear_sag_mm} onChange={value => setField('rear_sag_mm', value)} disabled={!canEdit} />
            <InputField label="Sag %" type="number" value={form.rear_sag_pct} onChange={value => setField('rear_sag_pct', value)} disabled={!canEdit} />
          </div>
          <div className="grid-4">
            <InputField label="Preload Turns" type="number" value={form.shock_preload_turns} onChange={value => setField('shock_preload_turns', value)} disabled={!canEdit} />
            <InputField label="Spacers" type="number" value={form.shock_spacers} onChange={value => setField('shock_spacers', value)} disabled={!canEdit} />
            <InputField label="LSC" type="number" value={form.shock_lsc} onChange={value => setField('shock_lsc', value)} disabled={!canEdit} />
            <InputField label="HSC" type="number" value={form.shock_hsc} onChange={value => setField('shock_hsc', value)} disabled={!canEdit} />
          </div>
          <div className="grid-2">
            <InputField label="Rebound" type="number" value={form.shock_rebound} onChange={value => setField('shock_rebound', value)} disabled={!canEdit} />
            <InputField label="Shock Mode" value={form.shock_mode} onChange={value => setField('shock_mode', value)} disabled={!canEdit} />
          </div>
          <div className="grid-2">
            <InputField label="HBO / HBC / ABO" value={form.shock_hbo} onChange={value => setField('shock_hbo', value)} disabled={!canEdit} />
          </div>
        </div>
      ) : null}

      <div className="grid-2">
        <InputField label="Front Tyre Pressure" type="number" value={form.tyre_pressure_front} onChange={value => setField('tyre_pressure_front', value)} disabled={!canEdit} />
        <InputField label="Rear Tyre Pressure" type="number" value={form.tyre_pressure_rear} onChange={value => setField('tyre_pressure_rear', value)} disabled={!canEdit} />
      </div>

      <Field label="Setup Notes">
        <textarea value={form.notes || ''} onChange={event => setField('notes', event.target.value)} disabled={!canEdit} placeholder="Rider notes, workshop context or setup purpose..." />
      </Field>

      {canEdit ? (
        <div className="action-row">
          {onCancel ? <button className="btn btn-ghost" type="button" onClick={onCancel}>Cancel</button> : null}
          <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : submitLabel}</button>
        </div>
      ) : null}
    </form>
  )
}
